import { z } from 'zod';
import { walkMigrations } from './migrate.js';
import { THEME_KEYS } from './theme.js';
/**
 * The complete, closed set of things the activity log records.
 *
 * Closed rather than open because every writer and every reader share this
 * list — an action neither side knows about cannot be described. Adding one
 * is an edit here, in `ActivityDetailsSchemas` and in
 * `ACTIVITY_DETAILS_VERSION` together, never a caller inventing a string.
 */
export const ACTIVITY_ACTIONS = [
    'report.created',
    'report.updated',
    'report.restored',
    'report.refreshed',
    'report.deleted',
    'dataset.created',
    'snapshot.submitted',
    'theme.updated',
    'report.published',
    'report.republished',
    'report.unpublished',
    'report.remixed',
    'workspace.plan_changed',
    'source.connected',
    'source.disconnected',
    'source.needs_reconnect',
    'dataset.linked',
    'dataset.unlinked',
    'dataset.link_unavailable',
    'workspace.deleted',
    'workspace.restored',
    'workspace.purged'
];
function isActivityAction(value) {
    return ACTIVITY_ACTIONS.includes(value);
}
/**
 * The `details_version: 1` payload shape for each action. `.strict()` so an
 * extra or misspelled field fails loudly at write time rather than being
 * silently dropped and then missing when the row is read back.
 */
export const ActivityDetailsSchemas = {
    'report.created': z
        .object({ revision: z.literal(1), datasetId: z.string(), snapshotId: z.string().nullable().optional() })
        .strict(),
    'report.updated': z
        .object({
        revisionBefore: z.number().int().positive(),
        revisionAfter: z.number().int().positive(),
        operations: z.array(z.string()).optional()
    })
        .strict(),
    'report.restored': z
        .object({
        revisionBefore: z.number().int(),
        revisionAfter: z.number().int(),
        restoredFrom: z.number().int(),
        restoredFromCreatedAt: z.string()
    })
        .strict(),
    'report.refreshed': z.object({ snapshotBefore: z.string().nullable(), snapshotAfter: z.string() }).strict(),
    'report.deleted': z.object({ title: z.string(), lastRevision: z.number().int() }).strict(),
    'dataset.created': z.object({ name: z.string() }).strict(),
    'snapshot.submitted': z
        .object({
        snapshotId: z.string(),
        rowCount: z.number().int(),
        via: z.enum(['rows', 'csv', 'file', 'google_sheets']).optional(),
        fetchedHost: z.string().max(253).optional()
    })
        .strict(),
    'theme.updated': z.object({ fields: z.array(z.enum(THEME_KEYS)).min(1) }).strict(),
    'report.published': z.object({ linkId: z.string(), revision: z.number().int(), snapshotId: z.string() }).strict(),
    'report.republished': z
        .object({
        linkId: z.string(),
        revision: z.number().int(),
        snapshotId: z.string(),
        version: z.number().int(),
        cause: z.literal('auto_update').optional()
    })
        .strict(),
    'report.unpublished': z
        .object({
        linkId: z.string(),
        cause: z.enum(['workspace_deleted', 'account_deleted', 'operator']).optional(),
        operator: z.string().optional(),
        reason: z.string().optional()
    })
        .strict(),
    'report.remixed': z.object({ sourceLinkId: z.string(), datasetId: z.string() }).strict(),
    'workspace.plan_changed': z.object({ from: z.string(), to: z.string() }).strict(),
    'source.connected': z.object({ kind: z.literal('google'), accountEmail: z.string() }).strict(),
    'source.disconnected': z
        .object({ kind: z.literal('google'), cause: z.enum(['workspace_deleted', 'account_deleted']).optional() })
        .strict(),
    'source.needs_reconnect': z.object({ kind: z.literal('google') }).strict(),
    'dataset.linked': z.object({ kind: z.literal('google_sheets'), title: z.string(), tabTitle: z.string() }).strict(),
    'dataset.unlinked': z.object({ kind: z.literal('google_sheets') }).strict(),
    'dataset.link_unavailable': z
        .object({ kind: z.literal('google_sheets'), status: z.union([z.literal(403), z.literal(404)]) })
        .strict(),
    'workspace.deleted': z
        .object({
        name: z.string(),
        via: z.enum(['workspace', 'account']),
        purgeAfter: z.string(),
        linksRemoved: z.number().int(),
        tokensRevoked: z.number().int(),
        sessionsMoved: z.number().int(),
        sourcesDisconnected: z.number().int()
    })
        .strict(),
    'workspace.restored': z.object({ deletedAt: z.string(), via: z.enum(['workspace', 'account']), reason: z.string() }).strict(),
    'workspace.purged': z
        .object({
        deletedAt: z.string(),
        rows: z
            .object({
            reports: z.number().int(),
            revisions: z.number().int(),
            datasets: z.number().int(),
            snapshots: z.number().int(),
            links: z.number().int(),
            tokens: z.number().int(),
            events: z.number().int()
        })
            .strict(),
        blobs: z.number().int(),
        reason: z.string().optional()
    })
        .strict()
};
/** Current `details_version` per action. All start at 1 — nothing has ever
 *  been stored at another version, same reasoning as `DEFINITION_MIGRATIONS`
 *  in migrate.ts. */
export const ACTIVITY_DETAILS_VERSION = {
    'report.created': 1,
    'report.updated': 1,
    'report.restored': 1,
    'report.refreshed': 1,
    'report.deleted': 1,
    'dataset.created': 1,
    'snapshot.submitted': 1,
    'theme.updated': 1,
    'report.published': 1,
    'report.republished': 1,
    'report.unpublished': 1,
    'report.remixed': 1,
    'workspace.plan_changed': 1,
    'source.connected': 1,
    'source.disconnected': 1,
    'source.needs_reconnect': 1,
    'dataset.linked': 1,
    'dataset.unlinked': 1,
    'dataset.link_unavailable': 1,
    'workspace.deleted': 1,
    'workspace.restored': 1,
    'workspace.purged': 1
};
/** Migration steps between adjacent `details_version` values, keyed by
 *  action. Every chain starts empty, for the same reason
 *  `DEFINITION_MIGRATIONS` starts empty: the path exists before it is ever
 *  needed, so the first real version bump lands with it already proven. */
export const ACTIVITY_DETAILS_MIGRATIONS = {
    'report.created': {},
    'report.updated': {},
    'report.restored': {},
    'report.refreshed': {},
    'report.deleted': {},
    'dataset.created': {},
    'snapshot.submitted': {},
    'theme.updated': {},
    'report.published': {},
    'report.republished': {},
    'report.unpublished': {},
    'report.remixed': {},
    'workspace.plan_changed': {},
    'source.connected': {},
    'source.disconnected': {},
    'source.needs_reconnect': {},
    'dataset.linked': {},
    'dataset.unlinked': {},
    'dataset.link_unavailable': {},
    'workspace.deleted': {},
    'workspace.restored': {},
    'workspace.purged': {}
};
/**
 * What survives when a deleted workspace's activity rows are scrubbed (spec
 * "Scrubbing"): per action, the details fields kept and the fixed summary the
 * row gets. A field not listed is dropped. Ids are `pseudonym`, never `keep`.
 * `satisfies` makes an action without an entry fail to compile.
 */
export const ACTIVITY_SCRUB = {
    'report.created': { fields: { revision: 'keep', datasetId: 'pseudonym', snapshotId: 'pseudonym' }, summary: 'Created the report' },
    'report.updated': { fields: { revisionBefore: 'keep', revisionAfter: 'keep' }, summary: 'Edited the report' },
    'report.restored': {
        fields: { revisionBefore: 'keep', revisionAfter: 'keep', restoredFrom: 'keep', restoredFromCreatedAt: 'keep' },
        summary: 'Restored an earlier version'
    },
    'report.refreshed': { fields: { snapshotBefore: 'pseudonym', snapshotAfter: 'pseudonym' }, summary: 'Switched to newer data' },
    'report.deleted': { fields: { lastRevision: 'keep' }, summary: 'Deleted the report' },
    'dataset.created': { fields: {}, summary: 'Added a data source' },
    'snapshot.submitted': { fields: { snapshotId: 'pseudonym', rowCount: 'keep', via: 'keep' }, summary: 'Uploaded new data' },
    'theme.updated': { fields: { fields: 'keep' }, summary: 'Changed the report theme' },
    'report.published': {
        fields: { linkId: 'pseudonym', revision: 'keep', snapshotId: 'pseudonym' },
        summary: 'Published a link'
    },
    'report.republished': {
        fields: { linkId: 'pseudonym', revision: 'keep', snapshotId: 'pseudonym', version: 'keep', cause: 'keep' },
        summary: 'Updated the published version'
    },
    'report.unpublished': { fields: { linkId: 'pseudonym', cause: 'keep' }, summary: 'Unpublished the link' },
    'report.remixed': { fields: { sourceLinkId: 'pseudonym', datasetId: 'pseudonym' }, summary: 'Remixed from a published report' },
    'workspace.plan_changed': { fields: { from: 'keep', to: 'keep' }, summary: 'Changed the plan' },
    'source.connected': { fields: { kind: 'keep' }, summary: 'Connected Google' },
    'source.disconnected': { fields: { kind: 'keep', cause: 'keep' }, summary: 'Disconnected Google' },
    'source.needs_reconnect': { fields: { kind: 'keep' }, summary: 'Google needs reconnecting' },
    'dataset.linked': { fields: { kind: 'keep' }, summary: 'Linked a Google Sheet' },
    'dataset.unlinked': { fields: { kind: 'keep' }, summary: 'Unlinked a Google Sheet' },
    'dataset.link_unavailable': { fields: { kind: 'keep', status: 'keep' }, summary: 'The linked Sheet is unavailable' },
    'workspace.deleted': {
        fields: {
            via: 'keep',
            purgeAfter: 'keep',
            linksRemoved: 'keep',
            tokensRevoked: 'keep',
            sessionsMoved: 'keep',
            sourcesDisconnected: 'keep'
        },
        summary: 'Deleted the workspace'
    },
    'workspace.restored': { fields: { deletedAt: 'keep', via: 'keep' }, summary: 'Restored the workspace' },
    'workspace.purged': { fields: { deletedAt: 'keep', rows: 'keep', blobs: 'keep' }, summary: 'Workspace purged' }
};
/** Fields common to every activity event, regardless of action. */
const activityEventCommonShape = {
    workspaceId: z.string(),
    actorPrincipalId: z.string().nullable(),
    actorType: z.string().nullable(),
    actorLabel: z.string().nullable(),
    onBehalfOfPrincipalId: z.string().nullable(),
    resourceType: z.string(),
    resourceId: z.string(),
    correlationId: z.string().nullable(),
    summary: z.string()
};
/** An activity event as written. One arm per action: the common fields plus
 *  that action's own details shape. */
export const ActivityEventSchema = z.discriminatedUnion('action', [
    z.object({ ...activityEventCommonShape, action: z.literal('report.created'), details: ActivityDetailsSchemas['report.created'] }),
    z.object({ ...activityEventCommonShape, action: z.literal('report.updated'), details: ActivityDetailsSchemas['report.updated'] }),
    z.object({ ...activityEventCommonShape, action: z.literal('report.restored'), details: ActivityDetailsSchemas['report.restored'] }),
    z.object({ ...activityEventCommonShape, action: z.literal('report.refreshed'), details: ActivityDetailsSchemas['report.refreshed'] }),
    z.object({ ...activityEventCommonShape, action: z.literal('report.deleted'), details: ActivityDetailsSchemas['report.deleted'] }),
    z.object({ ...activityEventCommonShape, action: z.literal('dataset.created'), details: ActivityDetailsSchemas['dataset.created'] }),
    z.object({
        ...activityEventCommonShape,
        action: z.literal('snapshot.submitted'),
        details: ActivityDetailsSchemas['snapshot.submitted']
    }),
    z.object({ ...activityEventCommonShape, action: z.literal('theme.updated'), details: ActivityDetailsSchemas['theme.updated'] }),
    z.object({ ...activityEventCommonShape, action: z.literal('report.published'), details: ActivityDetailsSchemas['report.published'] }),
    z.object({ ...activityEventCommonShape, action: z.literal('report.republished'), details: ActivityDetailsSchemas['report.republished'] }),
    z.object({ ...activityEventCommonShape, action: z.literal('report.unpublished'), details: ActivityDetailsSchemas['report.unpublished'] }),
    z.object({ ...activityEventCommonShape, action: z.literal('report.remixed'), details: ActivityDetailsSchemas['report.remixed'] }),
    z.object({ ...activityEventCommonShape, action: z.literal('workspace.plan_changed'), details: ActivityDetailsSchemas['workspace.plan_changed'] }),
    z.object({ ...activityEventCommonShape, action: z.literal('source.connected'), details: ActivityDetailsSchemas['source.connected'] }),
    z.object({ ...activityEventCommonShape, action: z.literal('source.disconnected'), details: ActivityDetailsSchemas['source.disconnected'] }),
    z.object({ ...activityEventCommonShape, action: z.literal('source.needs_reconnect'), details: ActivityDetailsSchemas['source.needs_reconnect'] }),
    z.object({ ...activityEventCommonShape, action: z.literal('dataset.linked'), details: ActivityDetailsSchemas['dataset.linked'] }),
    z.object({ ...activityEventCommonShape, action: z.literal('dataset.unlinked'), details: ActivityDetailsSchemas['dataset.unlinked'] }),
    z.object({ ...activityEventCommonShape, action: z.literal('dataset.link_unavailable'), details: ActivityDetailsSchemas['dataset.link_unavailable'] }),
    z.object({ ...activityEventCommonShape, action: z.literal('workspace.deleted'), details: ActivityDetailsSchemas['workspace.deleted'] }),
    z.object({ ...activityEventCommonShape, action: z.literal('workspace.restored'), details: ActivityDetailsSchemas['workspace.restored'] }),
    z.object({ ...activityEventCommonShape, action: z.literal('workspace.purged'), details: ActivityDetailsSchemas['workspace.purged'] })
]);
/**
 * Reads one row's details, tolerantly.
 *
 * Never throws (spec "Reading — tolerant"): an unknown action, a
 * `details_version` newer than this build understands, unparseable JSON or a
 * payload that fails its schema all yield `details: null` rather than
 * surfacing an error, so one corrupt or forward-written row cannot take the
 * rest of the feed down with it.
 */
export function readActivityEvent(row) {
    const { action } = row;
    if (!isActivityAction(action)) {
        return { details: null };
    }
    try {
        const raw = JSON.parse(row.detailsJson);
        const walked = walkMigrations(raw, {
            versionOf: () => row.detailsVersion,
            target: ACTIVITY_DETAILS_VERSION[action],
            steps: ACTIVITY_DETAILS_MIGRATIONS[action],
            onNewer: 'null',
            fail: (message) => new Error(message)
        });
        if (walked === null) {
            return { details: null };
        }
        const parsed = ActivityDetailsSchemas[action].safeParse(walked);
        return parsed.success ? { details: parsed.data } : { details: null };
    }
    catch {
        return { details: null };
    }
}
//# sourceMappingURL=activity.js.map